import { z } from 'zod'
import _ from 'lodash'

export enum WeaknessEtiology {
	Stroke = "Stroke",
	BrainTrauma = "Brain trauma",
	SpinalCordInjury = "Spinal cord injury"
}

export enum WeaknessType {
	Paralysis = "Plegia (paralysis)",
	Weakness = "Paresis (partial paralysis / weakness)",
	//MuscleWasting = "Muscle wasting",
}

export enum WeaknessSite {
	RightUpperArm = "Right upper limb",
	RightLowerForearm = "Right lower/forearm",
	RightUpperLegThigh = "Right upper leg/thigh",
	RightLowerLeg = "Right lower limb",
	HemiRightSide = "Hemi, right side",
	LeftUpperArm = "Left upper limb",
	LeftLowerForearm = "Left lower/forearm",
	LeftUpperLegThigh = "Left upper leg/thigh",
	LeftLowerLeg = "Left lower limb",
	HemiLeftSide = "Hemi, left side",
	UpperLimbsBothArms = "Upper limbs (both arms)",
	LowerLimbsBothLegs = "Lower limbs (both legs)",
	QuadFourLimbs = "Quad, four limbs",
	FourLimbs = "All limbs",
	RightFacial = "Right facial",
	LeftFacial = "Left facial",
}

export enum WeaknessSide {
	Left = "Left",
	Right = "Right",
	Quad = "Quadriplegi",
	All = "All Limbs",
	Both = "Both",
	NA = "N/A"
}

export const RWeaknessEtiology = z.enum(WeaknessEtiology)
export const RWeaknessType = z.enum(WeaknessType)
export const RWeaknessSite = z.enum(WeaknessSite)

export const RNeurologicWeaknessItem = z.object({
	etiology: RWeaknessEtiology,
	type: RWeaknessType,
	site: RWeaknessSite,
	code1: z.string(),
	code2: z.nullable(z.string())
})

export type NeurologicWeaknessItem = z.infer<typeof RNeurologicWeaknessItem>

export const getAffectedSide = (site: string) => {
	if(!site) return WeaknessSide.NA

	const siteLower = site.toLowerCase()

	if(siteLower.includes('left')) {
		return WeaknessSide.Left
	}
	else if(siteLower.includes('right')) {
		return WeaknessSide.Right
	}
	else if(siteLower.includes('quad')) {
		return WeaknessSide.Quad
	}
	else if(siteLower.includes('all')) {
		return WeaknessSide.All
	}
	else if(siteLower.includes('both')) {
		return WeaknessSide.Both
	}

	return WeaknessSide.NA
}

export const getEtiologiesFromSite = (site: string) => {
	return _.uniq(NEUROLOGIC_WEAKNESS_CODING_MAP.filter(mapping => mapping.site === site)
		.map(mapping => mapping.etiology))
}

export const getWeaknessTypeFromSiteAndEtiology = (site: string, etiology: string) => {
	return _.uniq(NEUROLOGIC_WEAKNESS_CODING_MAP.filter(mapping => mapping.site === site && mapping.etiology === etiology)
		.map(mapping => mapping.type))
}

export const getMapping = (site: string, etiology: string, type: string) => {
	return NEUROLOGIC_WEAKNESS_CODING_MAP.find(mapping => 
		mapping.site === site &&
		mapping.etiology === etiology &&
		mapping.type === type
	)
}

export const NEUROLOGIC_WEAKNESS_CODING_MAP: NeurologicWeaknessItem[] = [
	{
	  "etiology": WeaknessEtiology.Stroke,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.RightUpperArm,
	  "code1": "I69331",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.Stroke,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.RightLowerForearm,
	  "code1": "I69331",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.Stroke,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.RightUpperLegThigh,
	  "code1": "I69341",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.Stroke,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.RightLowerLeg,
	  "code1": "I69341",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.Stroke,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.HemiRightSide,
	  "code1": "I69351",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.Stroke,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.LeftUpperArm,
	  "code1": "I69334",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.Stroke,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.LeftLowerForearm,
	  "code1": "I69334",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.Stroke,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.LeftUpperLegThigh,
	  "code1": "I69344",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.Stroke,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.LeftLowerLeg,
	  "code1": "I69344",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.Stroke,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.HemiLeftSide,
	  "code1": "I69354",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.Stroke,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.UpperLimbsBothArms,
	  "code1": "ISG830A",
	  "code2": null,
	},
	{
	  "etiology": WeaknessEtiology.Stroke,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.LowerLimbsBothLegs,
	  "code1": "ISG8220L",
	  "code2": null,
	},
	{
	  "etiology": WeaknessEtiology.Stroke,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.FourLimbs,
	  "code1": "ISG8250Q",
	  "code2": null,
	},
	{
	  "etiology": WeaknessEtiology.Stroke,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.RightFacial,
	  "code1": "I69392",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.Stroke,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.LeftFacial,
	  "code1": "I69392",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.Stroke,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.RightUpperArm,
	  "code1": "I69331",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.Stroke,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.RightLowerForearm,
	  "code1": "I69331",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.Stroke,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.RightUpperLegThigh,
	  "code1": "I69341",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.Stroke,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.RightLowerLeg,
	  "code1": "I69341",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.Stroke,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.HemiRightSide,
	  "code1": "I69351",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.Stroke,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.LeftUpperArm,
	  "code1": "I69334",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.Stroke,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.LeftLowerForearm,
	  "code1": "I69334",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.Stroke,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.LeftUpperLegThigh,
	  "code1": "I69344",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.Stroke,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.LeftLowerLeg,
	  "code1": "I69344",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.Stroke,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.HemiLeftSide,
	  "code1": "I69354",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.Stroke,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.UpperLimbsBothArms,
	  "code1": "ISG830A",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.Stroke,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.LowerLimbsBothLegs,
	  "code1": "ISG8220L",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.Stroke,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.FourLimbs,
	  "code1": "ISG8250Q",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.Stroke,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.RightFacial,
	  "code1": "I69392",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.Stroke,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.LeftFacial,
	  "code1": "I69392",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.BrainTrauma,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.RightUpperArm,
	  "code1": "G8321",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.BrainTrauma,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.RightLowerForearm,
	  "code1": "G8321",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.BrainTrauma,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.RightUpperLegThigh,
	  "code1": "G8311",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.BrainTrauma,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.RightLowerLeg,
	  "code1": "G8311",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.BrainTrauma,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.HemiRightSide,
	  "code1": "G8191",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.BrainTrauma,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.LeftUpperArm,
	  "code1": "G8324",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.BrainTrauma,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.LeftLowerForearm,
	  "code1": "G8324",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.BrainTrauma,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.LeftUpperLegThigh,
	  "code1": "G8314",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.BrainTrauma,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.LeftLowerLeg,
	  "code1": "G8314",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.BrainTrauma,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.HemiLeftSide,
	  "code1": "G8194",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.BrainTrauma,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.UpperLimbsBothArms,
	  "code1": "G830",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.BrainTrauma,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.LowerLimbsBothLegs,
	  "code1": "G8220",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.BrainTrauma,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.FourLimbs,
	  "code1": "G8250",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.BrainTrauma,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.RightFacial,
	  "code1": "G510",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.BrainTrauma,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.LeftFacial,
	  "code1": "G510",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.BrainTrauma,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.RightUpperArm,
	  "code1": "G8321",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.BrainTrauma,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.RightLowerForearm,
	  "code1": "G8321",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.BrainTrauma,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.RightUpperLegThigh,
	  "code1": "G8311",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.BrainTrauma,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.RightLowerLeg,
	  "code1": "G8311",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.BrainTrauma,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.HemiRightSide,
	  "code1": "G8191",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.BrainTrauma,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.LeftUpperArm,
	  "code1": "G8324",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.BrainTrauma,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.LeftLowerForearm,
	  "code1": "G8324",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.BrainTrauma,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.LeftUpperLegThigh,
	  "code1": "G8314",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.BrainTrauma,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.LeftLowerLeg,
	  "code1": "G8314",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.BrainTrauma,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.HemiLeftSide,
	  "code1": "G8194",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.BrainTrauma,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.UpperLimbsBothArms,
	  "code1": "G830",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.BrainTrauma,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.LowerLimbsBothLegs,
	  "code1": "G8220",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.BrainTrauma,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.FourLimbs,
	  "code1": "G8250",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.BrainTrauma,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.RightFacial,
	  "code1": "G510",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.BrainTrauma,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.LeftFacial,
	  "code1": "G510",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.SpinalCordInjury,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.RightUpperArm,
	  "code1": "G8321",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.SpinalCordInjury,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.RightLowerForearm,
	  "code1": "G8321",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.SpinalCordInjury,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.RightUpperLegThigh,
	  "code1": "G8311",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.SpinalCordInjury,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.RightLowerLeg,
	  "code1": "G8311",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.SpinalCordInjury,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.HemiRightSide,
	  "code1": "G8191",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.SpinalCordInjury,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.LeftUpperArm,
	  "code1": "G8324",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.SpinalCordInjury,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.LeftLowerForearm,
	  "code1": "G8324",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.SpinalCordInjury,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.LeftUpperLegThigh,
	  "code1": "G8314",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.SpinalCordInjury,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.LeftLowerLeg,
	  "code1": "G8314",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.SpinalCordInjury,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.HemiLeftSide,
	  "code1": "G8194",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.SpinalCordInjury,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.UpperLimbsBothArms,
	  "code1": "G830",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.SpinalCordInjury,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.LowerLimbsBothLegs,
	  "code1": "G8220",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.SpinalCordInjury,
	  "type": WeaknessType.Paralysis,
	  "site": WeaknessSite.FourLimbs,
	  "code1": "G8250",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.SpinalCordInjury,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.RightUpperArm,
	  "code1": "G8321",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.SpinalCordInjury,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.RightLowerForearm,
	  "code1": "G8321",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.SpinalCordInjury,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.RightUpperLegThigh,
	  "code1": "G8311",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.SpinalCordInjury,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.RightLowerLeg,
	  "code1": "G8311",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.SpinalCordInjury,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.HemiRightSide,
	  "code1": "G8191",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.SpinalCordInjury,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.LeftUpperArm,
	  "code1": "G8324",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.SpinalCordInjury,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.LeftLowerForearm,
	  "code1": "G8324",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.SpinalCordInjury,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.LeftUpperLegThigh,
	  "code1": "G8314",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.SpinalCordInjury,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.LeftLowerLeg,
	  "code1": "G8314",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.SpinalCordInjury,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.HemiLeftSide,
	  "code1": "G8194",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.SpinalCordInjury,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.UpperLimbsBothArms,
	  "code1": "G830",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.SpinalCordInjury,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.LowerLimbsBothLegs,
	  "code1": "G8220",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.SpinalCordInjury,
	  "type": WeaknessType.Weakness,
	  "site": WeaknessSite.FourLimbs,
	  "code1": "G8250",
	  "code2": null
	},
	/* muscle wasting removed with ticket 1300
	{
	  "etiology": WeaknessEtiology.Unknown,
	  "type": WeaknessType.MuscleWasting,
	  "site": WeaknessSite.RightUpperArm,
	  "code1": "M62521",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.Unknown,
	  "type": WeaknessType.MuscleWasting,
	  "site": WeaknessSite.RightLowerForearm,
	  "code1": "M62531",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.Unknown,
	  "type": WeaknessType.MuscleWasting,
	  "site": WeaknessSite.RightUpperLegThigh,
	  "code1": "M62551",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.Unknown,
	  "type": WeaknessType.MuscleWasting,
	  "site": WeaknessSite.RightLowerLeg,
	  "code1": "M62561",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.Unknown,
	  "type": WeaknessType.MuscleWasting,
	  "site": WeaknessSite.LeftUpperArm,
	  "code1": "M62522",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.Unknown,
	  "type": WeaknessType.MuscleWasting,
	  "site": WeaknessSite.LeftLowerForearm,
	  "code1": "M62532",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.Unknown,
	  "type": WeaknessType.MuscleWasting,
	  "site": WeaknessSite.LeftUpperLegThigh,
	  "code1": "M62552",
	  "code2": null
	},
	{
	  "etiology": WeaknessEtiology.Unknown,
	  "type": WeaknessType.MuscleWasting,
	  "site": WeaknessSite.LeftLowerLeg,
	  "code1": "M62562",
	  "code2": null
	}
	*/
];

